import type { NormalizedPoint as Point } from '../model';

export interface PathControl {
  readonly incoming?: Point;
  readonly outgoing?: Point;
}
export interface EditablePath {
  readonly points: readonly Point[];
  readonly closed?: boolean;
  readonly pathControls?: readonly PathControl[];
}
const mix = (a: Point, b: Point, t: number): Point => ({ x: a.x + (b.x - a.x) * t, y: a.y + (b.y - a.y) * t });
export function pathSegment(path: EditablePath, index: number): readonly [Point, Point, Point, Point] {
  const next = (index + 1) % path.points.length;
  const a = path.points[index]!,
    b = path.points[next]!;
  return [
    a,
    path.pathControls?.[index]?.outgoing ?? mix(a, b, 1 / 3),
    path.pathControls?.[next]?.incoming ?? mix(a, b, 2 / 3),
    b
  ];
}
export function pathAt(segment: readonly [Point, Point, Point, Point], t: number): Point {
  const [a, b, c, d] = segment;
  return mix(mix(mix(a, b, t), mix(b, c, t), t), mix(mix(b, c, t), mix(c, d, t), t), t);
}
export function pathData(path: EditablePath): string {
  if (!path.points.length) return '';
  let d = `M ${path.points[0]!.x} ${path.points[0]!.y}`;
  const count = path.points.length - (path.closed ? 0 : 1);
  for (let i = 0; i < count; i++) {
    const [, b, c, z] = pathSegment(path, i);
    const next = (i + 1) % path.points.length;
    d +=
      path.pathControls?.[i]?.outgoing || path.pathControls?.[next]?.incoming
        ? ` C ${b.x} ${b.y} ${c.x} ${c.y} ${z.x} ${z.y}`
        : ` L ${z.x} ${z.y}`;
  }
  return d + (path.closed ? ' Z' : '');
}
export function mapPathControls(controls: readonly PathControl[], map: (p: Point) => Point): PathControl[] {
  return controls.map((c) => ({
    ...(c.incoming ? { incoming: map(c.incoming) } : {}),
    ...(c.outgoing ? { outgoing: map(c.outgoing) } : {})
  }));
}
export function movePathNode<T extends EditablePath>(path: T, index: number, to: Point): T {
  const from = path.points[index]!;
  const move = (p: Point) => ({ x: p.x + to.x - from.x, y: p.y + to.y - from.y });
  return {
    ...path,
    points: path.points.map((p, i) => (i === index ? to : p)),
    ...(path.pathControls
      ? { pathControls: path.pathControls.map((c, i) => (i === index ? mapPathControls([c], move)[0]! : c)) }
      : {})
  };
}
export function movePathControl<T extends EditablePath>(
  path: T,
  index: number,
  side: 'incoming' | 'outgoing',
  to: Point
): T {
  return {
    ...path,
    pathControls: path.points.map((_, i) => ({ ...path.pathControls?.[i], ...(i === index ? { [side]: to } : {}) }))
  };
}
/** Split with de Casteljau so inserting a node does not change the authored curve. */
export function insertPathNode<T extends EditablePath>(path: T, index: number, t: number): T {
  const [a, b, c, d] = pathSegment(path, index);
  const ab = mix(a, b, t),
    bc = mix(b, c, t),
    cd = mix(c, d, t),
    abc = mix(ab, bc, t),
    bcd = mix(bc, cd, t),
    p = mix(abc, bcd, t);
  const controls = path.points.map((_, i) => ({ ...path.pathControls?.[i] }));
  controls[index] = { ...controls[index], outgoing: ab };
  const next = (index + 1) % path.points.length;
  controls[next] = { ...controls[next], incoming: cd };
  controls.splice(index + 1, 0, { incoming: abc, outgoing: bcd });
  const points = [...path.points];
  points.splice(index + 1, 0, p);
  return { ...path, points, pathControls: controls };
}
export function deletePathNodes<T extends EditablePath>(path: T, indices: ReadonlySet<number>): T {
  const kept = path.points.map((_, i) => i).filter((i) => !indices.has(i));
  if (kept.length < (path.closed ? 3 : 2) || kept.length === path.points.length) return path;
  return {
    ...path,
    points: kept.map((i) => path.points[i]!),
    ...(path.pathControls ? { pathControls: kept.map((i) => path.pathControls?.[i] ?? {}) } : {})
  };
}
/** Screen-space nearest point, so non-square paper and zoom do not bias insertion. */
export function nearestPathSegment(path: EditablePath, point: Point, size: { width: number; height: number }) {
  let best = { index: 0, t: 0.5, distance: Infinity };
  for (let index = 0; index < path.points.length - (path.closed ? 0 : 1); index++) {
    const segment = pathSegment(path, index);
    const distance = (t: number) => {
      const p = pathAt(segment, t);
      return Math.hypot((p.x - point.x) * size.width, (p.y - point.y) * size.height);
    };
    // Sample then refine locally; handles may make a segment loop back on itself.
    for (let k = 0; k <= 64; k++) {
      const t = k / 64,
        d = distance(t);
      if (d < best.distance) best = { index, t, distance: d };
    }
  }
  let lo = Math.max(0, best.t - 1 / 64),
    hi = Math.min(1, best.t + 1 / 64);
  const segment = pathSegment(path, best.index);
  const distance = (t: number) => {
    const p = pathAt(segment, t);
    return Math.hypot((p.x - point.x) * size.width, (p.y - point.y) * size.height);
  };
  for (let n = 0; n < 24; n++) {
    const a = lo + (hi - lo) / 3,
      b = hi - (hi - lo) / 3;
    if (distance(a) < distance(b)) hi = b;
    else lo = a;
  }
  const t = (lo + hi) / 2;
  return { ...best, t, distance: distance(t) };
}
